import * as React from "react";
import styles from "./MetaBox.module.scss";
import { cn } from "../../../helpers";
import { Button } from "../Button/Button";
import { Input } from "../Input/Input";
import EditIcon from "../../icons/Edit";
import SaveCheckIcon from "../../icons/SaveCheck";
import CancelIcon from "../../icons/Cancel";

export interface MetaBoxProps {
    icon?: React.ReactNode;
    label?: React.ReactNode;
    value: React.ReactNode;
    className?: string;
    hasError?: boolean;
    /** Edit functionality */
    isEditing?: boolean;
    onEdit?: () => void;
    onSave?: () => void;
    onCancel?: () => void;
    onValueChange?: (value: string) => void;
    editValue?: string;
    showEditButton?: boolean;
}

export const MetaBox: React.FC<MetaBoxProps> = ({
    icon,
    label,
    value,
    className,
    hasError = false,
    isEditing = false,
    editValue,
    onEdit,
    onSave,
    onCancel,
    onValueChange,
    showEditButton,
}) => {
    const shouldShowEditButton = showEditButton ?? Boolean(onEdit);
    const currentEditValue = editValue ?? (typeof value === "string" ? value : "");

    const renderValue = () => {
        if (isEditing) {
            return (
                <div className={styles.editContainer}>
                    <Input
                        value={currentEditValue}
                        onChange={(e) => onValueChange?.(e.target.value)}
                        className={styles.editInput}
                    />

                    <Button
                        variant="normal"
                        onClick={onSave}
                        className={styles.editActionButton}
                        disabled={!onSave}
                    >
                        <SaveCheckIcon stroke="var(--color-primary-v2)" />
                    </Button>
                    <Button
                        variant="normal"
                        onClick={onCancel}
                        className={styles.editActionButton}
                        disabled={!onCancel}
                    >
                        <CancelIcon />
                    </Button>
                </div>
            );
        }

        return (
            <div className={styles.valueContainer}>
                <span className={styles.metaValue}>{value}</span>
                {shouldShowEditButton && onEdit && (
                    <Button
                        onClick={onEdit}
                        className={styles.editButton}
                        variant="normal"
                        title="Edit"
                        aria-label="Edit"
                    >
                        <EditIcon />
                    </Button>
                )}
            </div>
        );
    };

    return (
        <div
            className={cn(styles.metaBox, className, {
                [styles.editing]: isEditing,
                [styles.error]: hasError && !isEditing,
            })}
        >
            {icon}
            {label && (
                <span className={styles.metaLabel}>
                    {typeof label === "string" ? `${label}:` : label}
                </span>
            )}
            {renderValue()}
        </div>
    );
};
